import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  HiOutlineHome,
  HiOutlineClock,
  HiOutlineMagnifyingGlass,
  HiOutlineFunnel,
  HiOutlineArrowPath,
} from "react-icons/hi2";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { activityLogService } from "../services";
import { MAX_APPS_SEED } from "../data/maxAppsDefinitions";
import PageLoading from "../components/common/PageLoading";
import "./ActivityLog.css";

const ACTION_OPTIONS = ["All Actions", "Create", "Update", "Delete", "Login", "Other"];

const ActivityLog = () => {
  const [logs, setLogs] = useState([]);
  const [summary, setSummary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  
  // Filters
  const [moduleFilter, setModuleFilter] = useState("All Modules");
  const [actionFilter, setActionFilter] = useState("All Actions");
  const [userSearch, setUserSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [summaryDays, setSummaryDays] = useState(7);

  const loadLogs = useCallback(async (pageNum, isAppend = false) => {
    try {
      if (isAppend) setLoadingMore(true);
      else setLoading(true);

      const params = {
        page: pageNum,
        pageSize: 20,
        module: moduleFilter === "All Modules" ? null : moduleFilter,
        action: actionFilter === "All Actions" ? null : actionFilter,
        userName: userSearch.trim() || null,
        dateFrom: dateFrom || null,
        dateTo: dateTo || null,
      };

      const data = await activityLogService.getActivityLogs(params);
      
      if (isAppend) {
        setLogs(prev => [...prev, ...data.items]);
      } else {
        setLogs(data.items || []);
      }
      
      setHasMore(data.items.length === 20);
    } catch (err) {
      console.error("Failed to load logs:", err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [moduleFilter, actionFilter, userSearch, dateFrom, dateTo]);

  const loadSummary = useCallback(async () => {
    try {
      const data = await activityLogService.getActivitySummary(summaryDays);
      setSummary(data || []);
    } catch (err) {
      console.error("Failed to load summary:", err);
    }
  }, [summaryDays]);

  useEffect(() => {
    loadLogs(1, false);
    setPage(1);
  }, [loadLogs]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  const handleLoadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    loadLogs(nextPage, true);
  };

  const clearFilters = () => {
    setModuleFilter("All Modules");
    setActionFilter("All Actions");
    setUserSearch("");
    setDateFrom("");
    setDateTo("");
  };

  const formatTimestamp = (ts) => {
    if (!ts) return "";
    const date = new Date(ts);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return "just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getAvatarLetter = (name) => {
    if (!name) return "?";
    return name.charAt(0).toUpperCase();
  };

  return (
    <div className="activity-log-container">
      <nav className="breadcrumb">
        <Link to="/home" className="breadcrumb-item"><HiOutlineHome /> Home</Link>
        <span className="breadcrumb-sep">/</span>
        <span className="breadcrumb-current">Activity Log</span>
      </nav>

      <div className="activity-log-header">
        <div className="title-section">
          <h1>Activity Log</h1>
          <p className="subtitle">Track every action across the platform for complete visibility and auditing.</p>
        </div>
        <button className="btn-refresh" onClick={() => { loadLogs(1, false); loadSummary(); }}>
          <HiOutlineArrowPath /> Refresh
        </button>
      </div>

      {/* --- Summary Visualization --- */}
      <section className="activity-summary-section">
        <div className="summary-header">
          <div className="summary-title">
            <h3>Activity by Module</h3>
            <span className="summary-subtitle">Log counts across functional areas</span>
          </div>
          <select 
            value={summaryDays} 
            onChange={(e) => setSummaryDays(Number(e.target.value))}
            className="select-period"
          >
            <option value={7}>Last 7 Days</option>
            <option value={30}>Last 30 Days</option>
            <option value={90}>Last 90 Days</option>
          </select>
        </div>
        <div className="summary-chart-wrap">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={summary}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border-primary)" />
              <XAxis 
                dataKey="module" 
                axisLine={false} 
                tickLine={false} 
                tick={{ fill: 'var(--text-secondary)', fontSize: 11 }}
              />
              <YAxis 
                axisLine={false} 
                tickLine={false} 
                tick={{ fill: 'var(--text-secondary)', fontSize: 11 }}
              />
              <Tooltip 
                contentStyle={{ 
                  borderRadius: '8px', 
                  border: '1px solid var(--border-primary)', 
                  background: 'var(--bg-card)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                }}
                cursor={{ fill: 'rgba(0,0,0,0.05)' }}
              />
              <Bar 
                dataKey="count" 
                fill="#4F46E5" 
                radius={[4, 4, 0, 0]} 
                barSize={32}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </section>

      {/* --- Filters Control --- */}
      <div className="activity-filters-card">
        <div className="filters-header">
          <HiOutlineFunnel className="filter-icon" />
          <span>Filters</span>
          <button className="btn-clear-filters" onClick={clearFilters}>Clear all</button>
        </div>
        <div className="filters-grid">
          <div className="filter-group">
            <label>Module</label>
            <select value={moduleFilter} onChange={(e) => setModuleFilter(e.target.value)}>
              <option value="All Modules">All Modules</option>
              {MAX_APPS_SEED.map(app => (
                <option key={app.code} value={app.name}>{app.name}</option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <label>Action</label>
            <select value={actionFilter} onChange={(e) => setActionFilter(e.target.value)}>
              {ACTION_OPTIONS.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <label>Date From</label>
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
          </div>
          <div className="filter-group">
            <label>Date To</label>
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
          </div>
          <div className="filter-group filter-search">
            <label>Search User</label>
            <div className="search-input-wrap">
              <HiOutlineMagnifyingGlass className="search-icon" />
              <input 
                type="text" 
                placeholder="Name or email..." 
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>

      {/* --- Timeline Feed --- */}
      <div className="activity-timeline-section">
        <h3>Recent Updates</h3>
        
        {loading && logs.length === 0 ? (
          <div className="full-page-load">
            <PageLoading message="Loading activity logs..." />
          </div>
        ) : logs.length === 0 ? (
          <div className="timeline-empty">
            <div className="empty-icon">📂</div>
            <h4>No activity logs found</h4>
            <p>Try adjusting your search criteria or date filters.</p>
          </div>
        ) : (
          <>
            <div className="timeline-items">
              {logs.map((item) => (
                <div className="activity-item" key={item.logId}>
                  <div className="activity-marker">
                    <div className="activity-dot" data-action={item.action}></div>
                  </div>
                  <div className="activity-card">
                    <div className="activity-card-top">
                      <div className="user-info">
                        <div className="user-avatar">{getAvatarLetter(item.userName)}</div>
                        <span className="user-name">{item.userName}</span>
                      </div>
                      <div className="activity-badges">
                        <span className="badge-action" data-action={item.action}>{item.action}</span>
                        {item.moduleName && <span className="badge-module">{item.moduleName}</span>}
                      </div>
                    </div>
                    <p className="activity-description">
                      {item.description || `${item.action} performed on ${item.tableName || 'System'} #${item.recordId || ''}`}
                    </p>
                    <div className="activity-card-bottom">
                      <HiOutlineClock className="time-icon" />
                      <time className="activity-time">{formatTimestamp(item.timestamp)}</time>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {hasMore && (
              <div className="pagination-footer">
                <button 
                  className="btn-load-more" 
                  onClick={handleLoadMore} 
                  disabled={loadingMore}
                >
                  {loadingMore ? "Loading more..." : "Load older activity"}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ActivityLog;
